const REMOTE_FRIENDLY_CODES = new Set(["PT", "ES", "GR", "TH", "CR", "PA", "MY", "MX", "HR"]);
const STRONG_INFRASTRUCTURE_CODES = new Set(["PT", "ES", "GR", "HR", "MY", "MX", "CR", "PA", "SG", "NL", "DE", "CA", "AU", "NZ", "JP"]);

const REMOTE_COMPATIBILITY_COPY = {
  REMOTE_FRIENDLY: {
    label: "Remote-friendly",
    explanation: "Straightforward visa paths make setup easier.",
  },
  WORKABLE_WITH_PLANNING: {
    label: "Possible with setup",
    explanation: "Visa or time-zone planning adds setup work.",
  },
  LIMITED_COMPATIBILITY: {
    label: "Limited compatibility",
    explanation: "Visa or income rules make setup harder.",
  },
};

const REMOTE_LEVEL_RANK = {
  REMOTE_FRIENDLY: 3,
  WORKABLE_WITH_PLANNING: 2,
  LIMITED_COMPATIBILITY: 1,
};

const FAR_TIMEZONE_CODES = new Set(["TH", "MY", "SG", "JP"]);

export const getRemoteProfile = (summary = {}) => {
  if (summary.remote_profile) return summary.remote_profile;

  const occupation = String(summary.occupation || "").toLowerCase();
  const flexibility = summary.work_location_flexibility || "";
  const workIntent = summary.work_abroad_intent || "";
  const supportPaths = Array.isArray(summary.move_support_paths) ? summary.move_support_paths : [];
  const legacyRemoteSignal = workIntent === "remote_self_employed"
    || Boolean(summary.remote_work)
    || supportPaths.includes("remote_income")
    || supportPaths.includes("self_employment");

  if ((occupation.includes("retired") || occupation.includes("financially independent") || occupation.includes("passive income")) && !legacyRemoteSignal) {
    return "NOT_APPLICABLE";
  }

  if (occupation.includes("employed") || occupation.includes("self-employed") || occupation.includes("business")) {
    if (flexibility === "must_be_in_specific_location") return "NOT_REMOTE";
    if (flexibility === "fully_remote") return "REMOTE";
    if (flexibility === "some_flexibility") return "HYBRID";
    if (legacyRemoteSignal) return "REMOTE";
  }

  if (legacyRemoteSignal) return "REMOTE";

  return "";
};

export const isRemoteRelevant = (summary = {}) => ["REMOTE", "HYBRID"].includes(getRemoteProfile(summary));

export const getCountryRemoteCompatibilityLevel = (country = {}, summary = {}) => {
  if (!isRemoteRelevant(summary)) return "";

  const remoteProfile = getRemoteProfile(summary);
  const code = country.code || "";
  const visaDifficulty = country.visa_difficulty || "Moderate";
  const needsReliableInternet = Boolean(summary.need_reliable_internet);
  const hasStrongInfrastructure = STRONG_INFRASTRUCTURE_CODES.has(code);
  const isUsbBasedRemoteWorker = /united states|usa|u\.s\./i.test(String(summary.current_country || ""));

  if (REMOTE_FRIENDLY_CODES.has(code) && (hasStrongInfrastructure || !needsReliableInternet)) {
    if (isUsbBasedRemoteWorker && FAR_TIMEZONE_CODES.has(code)) {
      return "WORKABLE_WITH_PLANNING";
    }
    return "REMOTE_FRIENDLY";
  }

  if (visaDifficulty === "Hard" || (needsReliableInternet && !hasStrongInfrastructure)) {
    return remoteProfile === "HYBRID" && visaDifficulty !== "Hard"
      ? "WORKABLE_WITH_PLANNING"
      : "LIMITED_COMPATIBILITY";
  }

  return "WORKABLE_WITH_PLANNING";
};

export const getCountryRemoteCompatibilityLabel = (country = {}, summary = {}) => {
  const level = getCountryRemoteCompatibilityLevel(country, summary);
  if (!level) return "";
  const copy = REMOTE_COMPATIBILITY_COPY[level];
  return copy.explanation;
};

export const getRemoteDecisionContrast = (countries = [], summary = {}) => {
  if (!isRemoteRelevant(summary) || countries.length < 2) return "";

  const firstCountry = countries[0];
  const secondCountry = countries[1];
  const firstLevel = getCountryRemoteCompatibilityLevel(firstCountry, summary);
  const secondLevel = getCountryRemoteCompatibilityLevel(secondCountry, summary);

  if (!firstLevel || !secondLevel) return "";

  const firstCopy = REMOTE_COMPATIBILITY_COPY[firstLevel];
  const secondCopy = REMOTE_COMPATIBILITY_COPY[secondLevel];

  if (firstLevel === secondLevel) {
    return `${firstCountry.name} and ${secondCountry.name} are both ${firstCopy.label.toLowerCase()} — ${firstCopy.explanation}.`;
  }

  const strongerCountry = REMOTE_LEVEL_RANK[firstLevel] >= REMOTE_LEVEL_RANK[secondLevel] ? firstCountry : secondCountry;
  const strongerLevel = strongerCountry === firstCountry ? firstLevel : secondLevel;
  const otherCountry = strongerCountry === firstCountry ? secondCountry : firstCountry;
  const otherLevel = otherCountry === firstCountry ? firstLevel : secondLevel;
  const strongerCopy = REMOTE_COMPATIBILITY_COPY[strongerLevel];
  const otherCopy = REMOTE_COMPATIBILITY_COPY[otherLevel];

  return `${strongerCountry.name} is ${strongerCopy.label.toLowerCase()} — ${strongerCopy.explanation}, while ${otherCountry.name} is ${otherCopy.label.toLowerCase()} — ${otherCopy.explanation}.`;
};